import { pipe, ReadonlyArray, Runtime } from "fp";

import * as Data from "~/Data";
import * as Driver from "~/Driver";

export * as Action from "./Action";
export * as Request from "./Request";

export type Summary = Data.Reference.Reference & {
  readonly messageCount: number;
  readonly recentMessageCount: number;
  readonly mostRecentMessage?: string;
};

export namespace State {
  export type State = {
    readonly store: Data.Store.Store<Summary>;
  };

  export const initial = (): State => ({
    store: Data.Store.initial(),
  });
}

export const useSummary = (): Data.Store.Store<Summary> =>
  Data.Store.useSelector(({ messages: { summary } }) => summary.store);

export type DriverWithSummaries = {
  readonly driver: Driver.Driver;
  readonly summary?: Summary;
};

export const useDriversWithMessageSummaries = (): readonly DriverWithSummaries[] => {
  const drivers = Data.Store.toCache(Driver.useStore());
  const summaries = Data.Store.toCache(useSummary());

  return pipe(
    drivers,
    Data.Cache.toList((id, driver) => ({ ...driver, id })),
    ReadonlyArray.reduce(
      [] as readonly DriverWithSummaries[],
      (previous, driver) => [
        ...previous,
        { driver, summary: summaries.data[driver.id], show: true },
      ]
    )
  );
};

export namespace Response {
  export const runtime = () =>
    Runtime.strict({
      driver_id: Runtime.number(),
      message_count: Runtime.number(),
      recent_message_count: Runtime.number(),
      most_recent_message: Runtime.nullable(Runtime.string()),
    });

  export const decode = (
    response: Runtime.ToType<typeof runtime>
  ): Summary => ({
    id: `${response.driver_id}`,
    messageCount: response.message_count,
    recentMessageCount: response.recent_message_count,
    mostRecentMessage: response.most_recent_message
      ? response.most_recent_message
      : undefined,
  });
}
